import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { money, fmtDate, ordinal } from "@/lib/ui";
import useIsMobile from "@/lib/useIsMobile";
import usePersistentState from "@/lib/usePersistentState";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Trophy, Search, Loader2, DollarSign, Flame, TrendingUp, X } from "lucide-react";

const TABS = [
  { k: "riders", l: "Money Board", icon: Trophy },
  { k: "recent", l: "Recent Winners", icon: Flame },
  { k: "payouts", l: "Payouts", icon: DollarSign },
];

export default function Stats() {
  const m = useIsMobile();
  const [tab, setTab] = usePersistentState("rp_stats_tab", "riders");
  const title = m ? "text-slate-100" : "text-[#14181F]";
  const page = m ? "bg-[#0C1017] min-h-screen text-slate-200 pb-24" : "";

  return (
    <div className={page} data-testid="stats-page">
      <div className={`px-4 sm:px-6 py-5 border-b flex items-center gap-2 ${m ? "border-white/10" : "border-[#E4E7EB]"}`}>
        <TrendingUp className="w-6 h-6 text-[#C8000F]" />
        <h1 className={`font-display text-2xl font-extrabold tracking-tight ${title}`}>Pro Stats</h1>
      </div>
      <div className="p-4 sm:p-6 max-w-4xl">
        <div className="flex rounded-lg overflow-hidden border border-[#D7DBE0] w-fit mb-5" data-testid="stats-tabs">
          {TABS.map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)} data-testid={`stats-tab-${t.k}`}
              className={`flex items-center gap-1.5 text-sm font-semibold px-4 py-2 ${tab === t.k ? "bg-[#C8000F] text-white" : (m ? "bg-white/5 text-slate-300" : "bg-white text-slate-600")}`}>
              <t.icon className="w-4 h-4" /> {t.l}
            </button>
          ))}
        </div>
        {tab === "riders" && <Riders m={m} />}
        {tab === "recent" && <Recent m={m} />}
        {tab === "payouts" && <Payouts m={m} />}
      </div>
    </div>
  );
}

function Riders({ m }) {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [ev, setEv] = useState("");
  const [sel, setSel] = useState(null);
  const inp = m ? "bg-white/5 border-white/10 text-slate-100 placeholder-slate-500" : "bg-white border-[#D7DBE0] text-[#14181F]";
  const card = m ? "bg-white/5 border-white/10" : "bg-white border-[#E4E7EB]";
  const title = m ? "text-slate-100" : "text-[#14181F]";

  useEffect(() => { api.get(`/pro/riders?limit=100${ev ? `&event=${encodeURIComponent(ev)}` : ""}`).then((r) => setData(r.data)); }, [ev]);
  const list = useMemo(() => {
    if (!data) return [];
    const qq = q.trim().toLowerCase();
    return qq ? data.riders.filter((r) => (r.name || "").toLowerCase().includes(qq)) : data.riders;
  }, [data, q]);

  return (
    <>
      <div className="flex flex-col sm:flex-row gap-3 mb-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search a rider…" data-testid="rider-search" className={`w-full rounded-lg border pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-[#C8000F] ${inp}`} />
        </div>
        {data && (
          <select value={ev} onChange={(e) => setEv(e.target.value)} data-testid="rider-event" className={`rounded-lg border px-3 py-2.5 text-sm sm:w-52 ${inp}`}>
            <option value="">All events</option>
            {data.events.map((e) => <option key={e} value={e}>{e}</option>)}
          </select>
        )}
      </div>
      {!data ? <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#C8000F]" /></div> : (
        <div className="space-y-2">
          {list.map((r, i) => (
            <button key={r.rider_key} onClick={() => setSel(r.rider_key)} data-testid={`rider-row-${i}`}
              className={`w-full flex items-center gap-3 rounded-xl border p-3 text-left ${card}`}>
              <span className={`w-7 text-center font-display font-bold ${i < 3 ? "text-[#C8000F]" : "text-slate-400"}`}>{i + 1}</span>
              <div className="flex-1 min-w-0">
                <div className={`font-semibold ${title}`}>{r.name}</div>
                <div className="text-[11px] text-slate-400">{r.top_event}{r.wins ? ` · ${r.wins} win${r.wins > 1 ? "s" : ""}` : ""}</div>
              </div>
              <span className="font-display font-bold text-[#3B6B1E]">{money(Math.round(r.total_won))}</span>
            </button>
          ))}
        </div>
      )}
      <RiderDialog m={m} riderKey={sel} onClose={() => setSel(null)} />
    </>
  );
}

function RiderDialog({ m, riderKey, onClose }) {
  const [d, setD] = useState(null);
  const nav = useNavigate();
  useEffect(() => { if (riderKey) { setD(null); api.get(`/pro/riders/${encodeURIComponent(riderKey)}`).then((r) => setD(r.data)); } }, [riderKey]);
  const DLG = m ? "bg-[#161B22] text-slate-100 border border-white/10 max-w-lg" : "bg-white text-[#14181F] border border-[#E4E7EB] max-w-lg";
  const title = m ? "text-slate-100" : "text-[#14181F]";
  return (
    <Dialog open={!!riderKey} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className={DLG} data-testid="rider-dialog">
        {!d ? <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#C8000F]" /></div> : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display flex items-start justify-between gap-3 pr-12">
                <span className="min-w-0 truncate mt-0.5">{d.name}</span>
                <span className="text-right shrink-0">
                <span className="text-[#3B6B1E] font-bold">{money(Math.round(d.total_won))}</span>
                <span className="block text-[10px] font-normal text-slate-400 tracking-wide">Official CPRA standings</span>
              </span>
              </DialogTitle>
            </DialogHeader>
            <div className="max-h-[64vh] overflow-y-auto pr-1 space-y-4">
              <div className="flex flex-wrap gap-1.5">
                {d.by_event.map((e) => <span key={e.event} className={`text-[11px] px-2 py-1 rounded ${m ? "bg-white/5" : "bg-[#F1F3F5]"}`}>{e.event} <b className="text-[#3B6B1E]">{money(Math.round(e.won))}</b></span>)}
              </div>
              {d.upcoming.length > 0 && (
                <div>
                  <div className="text-[11px] font-bold uppercase tracking-wide text-slate-400 mb-1.5">Up next</div>
                  {d.upcoming.slice(0, 6).map((u, i) => (
                    <button key={i} onClick={() => { onClose(); nav(`/rodeo/${u.rodeo_id}`); }} className="w-full flex justify-between text-xs py-1.5 border-b border-dashed border-white/10 text-left">
                      <span className={title}>{u.rodeo_name} · {u.event}</span>
                      <span className="text-slate-400">{u.performance_date ? fmtDate(u.performance_date) : ""}</span>
                    </button>
                  ))}
                </div>
              )}
              <div>
                <div className="text-[11px] font-bold uppercase tracking-wide text-slate-400 mb-1.5">Results ({d.results.length})</div>
                {d.results.slice(0, 30).map((r, i) => (
                  <div key={i} className="flex items-center justify-between gap-2 text-xs py-1.5 border-b border-dashed border-white/10">
                    <div className="min-w-0"><span className={title}>{r.rodeo_name}</span><span className="text-slate-400"> · {r.event}</span></div>
                    <div className="shrink-0 flex items-center gap-2">
                      {ordinal(r.place) && <span className="font-bold text-[#3B6B1E]">{ordinal(r.place)}</span>}
                      {r.value && <span className="text-slate-400">{r.value}</span>}
                      {r.earnings > 0 && <span className="font-bold text-[#E68A2E]">{money(Math.round(r.earnings))}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Recent({ m }) {
  const [data, setData] = useState(null);
  const nav = useNavigate();
  const card = m ? "bg-white/5 border-white/10" : "bg-white border-[#E4E7EB]";
  const title = m ? "text-slate-100" : "text-[#14181F]";
  useEffect(() => { api.get("/results/recent?limit=60").then((r) => setData(r.data)); }, []);
  if (!data) return <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#C8000F]" /></div>;
  return (
    <div className="space-y-2">
      {data.winners.map((w, i) => (
        <button key={i} onClick={() => nav(`/rodeo/${w.rodeo_id}`)} data-testid={`winner-${i}`} className={`w-full flex items-center gap-3 rounded-xl border p-3 text-left ${card}`}>
          <Flame className="w-4 h-4 text-[#E68A2E] shrink-0" />
          <div className="flex-1 min-w-0">
            <div className={`font-semibold ${title}`}>{w.rider} <span className="text-slate-400 font-normal">· {w.event}</span></div>
            <div className="text-[11px] text-slate-400">{w.rodeo_name} · {ordinal(w.place)}{w.value ? ` · ${w.value}` : ""}{w.date ? ` · ${fmtDate(w.date)}` : ""}</div>
          </div>
          {w.earnings > 0 && <span className="font-display font-bold text-[#3B6B1E] shrink-0">{money(Math.round(w.earnings))}</span>}
        </button>
      ))}
    </div>
  );
}

function Payouts({ m }) {
  const [data, setData] = useState(null);
  const nav = useNavigate();
  const card = m ? "bg-white/5 border-white/10" : "bg-white border-[#E4E7EB]";
  const title = m ? "text-slate-100" : "text-[#14181F]";
  useEffect(() => { api.get("/pro/payouts").then((r) => setData(r.data)); }, []);
  if (!data) return <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#C8000F]" /></div>;
  return (
    <div className="space-y-2">
      <div className="text-xs text-slate-400 mb-1">Total paid out at each rodeo this season</div>
      {data.rodeos.map((r, i) => (
        <button key={r.rodeo_id} onClick={() => nav(`/rodeo/${r.rodeo_id}`)} data-testid={`payout-${i}`} className={`w-full flex items-center gap-3 rounded-xl border p-3 text-left ${card}`}>
          <span className="w-7 text-center font-display font-bold text-slate-400">{i + 1}</span>
          <div className="flex-1 min-w-0">
            <div className={`font-semibold ${title}`}>{r.name}</div>
            <div className="text-[11px] text-slate-400">{r.city}, {r.state}{r.added_money ? ` · ${money(r.added_money)} added` : ""}</div>
          </div>
          <span className="font-display font-bold text-[#3B6B1E] shrink-0">{money(r.total_paid)}</span>
        </button>
      ))}
    </div>
  );
}
